import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, AlertCircle, FileText, Eye, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Diagonal watermark overlay — visible deterrent against screenshots/screen-rec
// ---------------------------------------------------------------------------
function WatermarkOverlay({ text }) {
 // Build a 4×4 grid of repeated watermarks so screenshots always contain at least one
 const rows = [0, 1, 2, 3, 4];
 const cols = [0, 1, 2, 3];
 return (
 <div
 aria-hidden="true"
 style={{
 position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 40,
 overflow: 'hidden',
 }}
 data-testid="investor-watermark"
 >
 <div style={{ position: 'absolute', inset: 0, transform: 'rotate(-30deg)', transformOrigin: 'center' }}>
 {rows.map((r) =>
 cols.map((c) => (
 <div
 key={`${r}-${c}`}
 style={{
 position: 'absolute',
 top: `${r * 20}%`, left: `${c * 28 - 10}%`,
 fontSize: '12px', letterSpacing: '0.15em',
 color: 'rgba(30, 63, 51, 0.08)', // Deep Pine @ 8%
 fontFamily: 'ui-monospace, SFMono-Regular, monospace',
 whiteSpace: 'nowrap',
 }}
 >
 {text}
 </div>
 ))
 )}
 </div>
 </div>
 );
}

// ---------------------------------------------------------------------------
// Disable copy/print as a soft deterrent (cosmetic — easily bypassed)
// ---------------------------------------------------------------------------
function NoCopyStyles() {
 return (
 <style>{`
 .vh-investor-protected { -webkit-user-select: none; -ms-user-select: none; user-select: none; }
 .vh-investor-protected img { -webkit-user-drag: none; user-drag: none; pointer-events: none; }
 @media print { .vh-investor-protected { display: none !important; } }
 `}</style>
 );
}

// ---------------------------------------------------------------------------
// Lightweight markdown renderer — headings, lists, bold, paragraphs, tables.
// Returns React nodes (no dangerouslySetInnerHTML) — defence-in-depth even
// though source content is owner-curated.
// ---------------------------------------------------------------------------
function renderInline(text, keyPrefix = '') {
 // Tokenise text into React nodes for **bold**, *italic*, and `code`.
 if (!text) return null;
 const parts = [];
 let i = 0;
 const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
 let m;
 let idx = 0;
 while ((m = re.exec(text)) !== null) {
 if (m.index > i) parts.push(text.slice(i, m.index));
 const k = `${keyPrefix}-${idx++}`;
 if (m[2] !== undefined) parts.push(<strong key={k}>{m[2]}</strong>);
 else if (m[3] !== undefined) parts.push(<em key={k}>{m[3]}</em>);
 else if (m[4] !== undefined) parts.push(<code key={k} className="px-1 py-0.5 rounded bg-[var(--vh-bg-muted)] text-xs">{m[4]}</code>);
 i = m.index + m[0].length;
 }
 if (i < text.length) parts.push(text.slice(i));
 return parts;
}

function renderMarkdown(md) {
 if (!md) return [];
 const lines = md.split('\n');
 const out = [];
 let inTable = false;
 let tableRows = [];

 const flushTable = () => {
 if (!tableRows.length) return;
 const header = tableRows[0];
 const body = tableRows.slice(2); // skip separator row
 out.push(
 <div key={`tbl-${out.length}`} className="overflow-x-auto my-4">
 <table className="min-w-full text-xs border border-[var(--vh-border)]">
 <thead className="bg-[var(--vh-bg-muted)]">
 <tr>{header.map((h, i) => <th key={`th-${out.length}-${i}`} className="px-2 py-1 text-left font-semibold border-b border-[var(--vh-border)]">{renderInline(h, `thi-${i}`)}</th>)}</tr>
 </thead>
 <tbody>
 {body.map((r, ri) => (
 <tr key={`tr-${out.length}-${ri}`} className="border-b border-slate-100">
 {r.map((c, ci) => <td key={`td-${ri}-${ci}`} className="px-2 py-1 align-top text-[var(--vh-text-secondary)]">{renderInline(c, `tdi-${ri}-${ci}`)}</td>)}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 );
 tableRows = [];
 inTable = false;
 };

 lines.forEach((raw, idx) => {
 const line = raw.trimEnd();
 if (line.startsWith('|') && line.endsWith('|')) {
 inTable = true;
 tableRows.push(line.slice(1, -1).split('|').map((c) => c.trim()));
 return;
 }
 if (inTable) flushTable();

 if (line.startsWith('# ')) out.push(<h1 key={idx} className="text-3xl font-semibold tracking-tight mt-8 mb-3 text-[var(--vh-text-primary)]">{line.slice(2)}</h1>);
 else if (line.startsWith('## ')) out.push(<h2 key={idx} className="text-xl font-semibold mt-6 mb-2 text-[var(--vh-text-primary)]">{line.slice(3)}</h2>);
 else if (line.startsWith('### ')) out.push(<h3 key={idx} className="text-base font-semibold mt-4 mb-1 text-[var(--vh-text-primary)]">{line.slice(4)}</h3>);
 else if (line.startsWith('- ')) out.push(<li key={idx} className="ml-5 list-disc text-sm text-[var(--vh-text-secondary)]">{renderInline(line.slice(2), `li-${idx}`)}</li>);
 else if (line === '---') out.push(<hr key={idx} className="my-6 border-[var(--vh-border)]" />);
 else if (line.startsWith('> ')) out.push(<blockquote key={idx} className="border-l-4 border-blue-500 pl-4 my-3 italic text-[var(--vh-text-secondary)] text-sm">{renderInline(line.slice(2), `q-${idx}`)}</blockquote>);
 else if (line.trim() === '') out.push(<div key={idx} className="h-2" />);
 else out.push(<p key={idx} className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">{renderInline(line, `p-${idx}`)}</p>);
 });
 if (inTable) flushTable();
 return out;
}

// ---------------------------------------------------------------------------
// Main Investor Room page
// ---------------------------------------------------------------------------
export default function InvestorRoom() {
 const [params] = useSearchParams();
 const token = params.get('token') || '';
 const [meta, setMeta] = useState(null);
 const [content, setContent] = useState(null);
 const [error, setError] = useState('');
 const [agreedName, setAgreedName] = useState('');
 const [agreedTitle, setAgreedTitle] = useState('');
 const [agreedCheck, setAgreedCheck] = useState(false);
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => {
 if (!token) { setError('Missing access token. Please use the link provided to you.'); return; }
 axios.get(`${API}/investors/access?token=${encodeURIComponent(token)}`)
 .then((r) => {
 setMeta(r.data);
 setAgreedName(r.data.invitee_name || '');
 if (!r.data.nda_required) {
 // Already agreed — fetch content
 loadContent();
 }
 })
 .catch((err) => setError(err.response?.data?.detail || 'Unable to validate access.'));
 }, [token]);

 const loadContent = useCallback(async () => {
 try {
 const { data } = await axios.get(`${API}/investors/content?token=${encodeURIComponent(token)}`);
 setContent(data);
 // Fire-and-forget view beacon
 axios.post(`${API}/investors/access/beacon?token=${encodeURIComponent(token)}`, { section: 'root' }).catch(() => {});
 } catch (err) {
 setError(err.response?.data?.detail || 'Failed to load content.');
 }
 }, [token]);

 const agree = async () => {
 setSubmitting(true);
 try {
 await axios.post(`${API}/investors/access/agree?token=${encodeURIComponent(token)}`, {
 agreed_name: agreedName, agreed_title: agreedTitle,
 });
 setMeta((m) => ({ ...m, nda_required: false }));
 loadContent();
 } catch (err) {
 setError(err.response?.data?.detail || 'Could not record NDA agreement.');
 } finally { setSubmitting(false); }
 };

 // Disable right-click on this page only
 useEffect(() => {
 const handler = (e) => { if (content) e.preventDefault(); };
 document.addEventListener('contextmenu', handler);
 return () => document.removeEventListener('contextmenu', handler);
 }, [content]);

 // ------ Render: error / NDA gate / content ------
 if (error) {
 return (
 <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center p-4">
 <div className="max-w-md bg-[var(--vh-bg-surface)] border border-rose-200 dark:border-rose-900/40 rounded-xl p-8 text-center" data-testid="investor-error">
 <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" aria-hidden="true" />
 <h1 className="text-lg font-semibold text-[var(--vh-text-primary)] mb-2">Access unavailable</h1>
 <p className="text-sm text-[var(--vh-text-secondary)] mb-4">{error}</p>
 <Link to="/" className="text-sm text-[var(--vh-brand-primary)] hover:underline">Back to Vault Hire →</Link>
 </div>
 </div>
 );
 }

 if (!meta) {
 return (
 <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center" data-testid="investor-loading">
 <div className="text-[var(--vh-text-muted)]">Validating access…</div>
 </div>
 );
 }

 // ------ NDA gate ------
 if (meta.nda_required) {
 return (
 <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center p-4">
 <div className="max-w-3xl w-full bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl shadow-xl overflow-hidden" data-testid="nda-gate">
 <header className="px-8 py-6 border-b border-slate-100 flex items-center gap-3">
 <Lock className="w-5 h-5 text-[var(--vh-brand-primary)]" aria-hidden="true" />
 <div>
 <h1 className="text-lg font-semibold text-[var(--vh-text-primary)]">Confidential Investor Data Room</h1>
 <p className="text-xs text-[var(--vh-text-muted)]">
 Access for <strong>{meta.invitee_name}</strong> · {meta.invitee_firm}
 </p>
 </div>
 </header>

 <div className="p-8 space-y-5">
 <div className="bg-[var(--vh-bg-muted)] rounded-lg p-5 max-h-72 overflow-y-auto text-xs leading-relaxed text-[var(--vh-text-secondary)] whitespace-pre-wrap font-serif" data-testid="nda-text">
 {renderMarkdown(meta.nda_text)}
 </div>
 <div className="flex items-center gap-1 text-xs text-[var(--vh-text-muted)]">
 <ShieldCheck className="w-3 h-3" aria-hidden="true" />
 NDA hash: <code className="font-mono">{meta.nda_hash.slice(0, 16)}…</code>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-[var(--vh-text-muted)] mb-1">Your full name</label>
 <input
 type="text" value={agreedName} onChange={(e) => setAgreedName(e.target.value)}
 data-testid="nda-name-input"
 className="w-full px-3 py-2 rounded-md border border-[var(--vh-border)] bg-[var(--vh-bg-surface)] text-sm"
 required
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--vh-text-muted)] mb-1">Job title (optional)</label>
 <input
 type="text" value={agreedTitle} onChange={(e) => setAgreedTitle(e.target.value)}
 data-testid="nda-title-input"
 className="w-full px-3 py-2 rounded-md border border-[var(--vh-border)] bg-[var(--vh-bg-surface)] text-sm"
 />
 </div>
 </div>

 <label className="flex gap-2 items-start text-sm text-[var(--vh-text-secondary)]">
 <input type="checkbox" checked={agreedCheck} onChange={(e) => setAgreedCheck(e.target.checked)} data-testid="nda-checkbox" className="mt-1" />
 <span>
 I have read and agree to the Mutual Confidentiality Agreement above. I confirm I have the authority
 to bind {meta.invitee_firm}. I understand that my IP address, name, and timestamp will be recorded
 as evidence of agreement.
 </span>
 </label>

 <div className="flex items-center justify-between pt-2 border-t border-slate-100">
 <Link to="/" className="text-xs text-[var(--vh-text-muted)] hover:underline">Cancel and leave</Link>
 <button
 onClick={agree}
 disabled={!agreedCheck || !agreedName.trim() || submitting}
 data-testid="nda-agree-btn"
 className="px-5 py-2 rounded-md bg-[var(--vh-brand-primary)] text-white text-sm font-medium hover:bg-[var(--vh-brand-primary-hover)] transition disabled:opacity-40"
 >
 {submitting ? 'Recording…' : 'I agree — Enter data room'}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
 }

 // ------ Content view ------
 if (!content) {
 return <div className="min-h-screen bg-[var(--vh-bg-base)] flex items-center justify-center text-[var(--vh-text-muted)]">Loading data room…</div>;
 }

 return (
 <div className="min-h-screen bg-[var(--vh-bg-base)] relative vh-investor-protected" data-testid="investor-room">
 <NoCopyStyles />
 <WatermarkOverlay text={content.watermark} />

 <header className="bg-[var(--vh-bg-surface)] border-b border-[var(--vh-border)] relative z-10">
 <div className="container mx-auto px-4 py-5 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <ShieldCheck className="w-5 h-5 text-[var(--vh-brand-primary)]" aria-hidden="true" />
 <div>
 <h1 className="text-base font-semibold text-[var(--vh-text-primary)]">Vault Hire — Confidential</h1>
 <p className="text-xs text-[var(--vh-text-muted)]">{content.invitee_name} · {content.invitee_firm}</p>
 </div>
 </div>
 <div className="text-xs text-[var(--vh-text-muted)] flex items-center gap-2">
 <Eye className="w-3 h-3" aria-hidden="true" /> View tracked
 </div>
 </div>
 </header>

 <main className="container mx-auto px-4 py-8 max-w-4xl relative z-10">
 <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl p-6 mb-6 shadow-sm">
 <h2 className="text-2xl font-semibold text-[var(--vh-text-primary)]">{content.product_overview.tagline}</h2>
 <p className="mt-3 text-sm text-[var(--vh-text-secondary)] leading-relaxed">{content.product_overview.core_thesis}</p>
 </section>

 <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl p-6 mb-6 shadow-sm">
 <h3 className="text-base font-semibold mb-3 text-[var(--vh-text-primary)]">Defensible moats</h3>
 <ul className="space-y-2 text-sm text-[var(--vh-text-secondary)]">
 {content.product_overview.moats.map((m, i) => (
 <li key={`moat-${m.slice(0, 32)}-${i}`} className="flex gap-2"><span className="text-emerald-500">✓</span>{m}</li>
 ))}
 </ul>
 </section>

 <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl p-6 mb-6 shadow-sm">
 <h3 className="text-base font-semibold mb-3 text-[var(--vh-text-primary)]">Target segments</h3>
 <ul className="space-y-2 text-sm text-[var(--vh-text-secondary)]">
 {content.product_overview.target_segments.map((t, i) => (
 <li key={`seg-${t.slice(0, 32)}-${i}`} className="flex gap-2"><ArrowRight className="w-4 h-4 mt-0.5 text-[var(--vh-accent)] shrink-0" />{t}</li>
 ))}
 </ul>
 </section>

 <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl p-6 mb-6 shadow-sm">
 <h3 className="text-base font-semibold mb-3 text-[var(--vh-text-primary)]">Pricing & metrics</h3>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div>
 <p className="text-xs uppercase tracking-wider text-[var(--vh-text-muted)] mb-1">Tiers (GBP)</p>
 <ul className="space-y-1 text-[var(--vh-text-secondary)]">
 {content.headline_metrics.tiers.map((t, i) => <li key={`tier-${t.slice(0, 32)}-${i}`}>· {t}</li>)}
 </ul>
 </div>
 <div>
 <p className="text-xs uppercase tracking-wider text-[var(--vh-text-muted)] mb-1">PAYG add-ons</p>
 <ul className="space-y-1 text-[var(--vh-text-secondary)]">
 {content.headline_metrics.payg_addons.map((a, i) => <li key={`payg-${a.slice(0, 32)}-${i}`}>· {a}</li>)}
 </ul>
 </div>
 </div>
 <p className="mt-4 text-xs text-[var(--vh-text-muted)]">
 Annual discount: <strong className="text-[var(--vh-text-secondary)]">{content.headline_metrics.annual_discount}</strong> · Test coverage: <strong className="text-[var(--vh-text-secondary)]">{content.headline_metrics.test_coverage}</strong>
 </p>
 </section>

 <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-xl p-6 mb-6 shadow-sm">
 <div className="flex items-center gap-2 mb-3">
 <FileText className="w-4 h-4 text-[var(--vh-accent)]" aria-hidden="true" />
 <h3 className="text-base font-semibold text-[var(--vh-text-primary)]">Competitive Analysis</h3>
 </div>
 <div data-testid="competitive-analysis-rendered">
 {renderMarkdown(content.competitive_analysis_markdown)}
 </div>
 </section>

 <footer className="text-center text-xs text-[var(--vh-text-muted)] py-8">
 © Vault Hire Limited. This material is confidential and shared under NDA. All access is logged.
 Unauthorised disclosure may result in legal action.
 </footer>
 </main>
 </div>
 );
}
